import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { Col, Row } from 'antd';
import { keyBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { NumberWidgetTypes } from 'api/dashboard/widget';
import { useFilters } from 'components/organisms/tasks/use-filters';
import { getApiFilterTransformer } from 'components/pages/api-filter-transformer';
import { useDashboardQuery } from 'queries/dashboard';
import { useUpdateWidgetOrderMutation } from 'queries/widgets';
import { excludedProjectFieldsForFilter } from 'utils/project-fields/constants';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { ChartWidget } from './components/chart-widget';
import { NumberWidget } from './components/number-widget';
import { byNumberTypeWidget } from './components/utils';
import { DraggableComponent } from './components/widget-dnd';
import { ChartDndOverLay } from './components/widget-dnd-overlay';
import { Wrapper } from './components/wrapper';
import { DashBoardFilter } from './dashboard-filter/dashboard-filter';
import { EmptyState } from './empty-state';
import { DASHBOARD_CONTAINER_ID } from './scroll';

interface DashboardProps {
  dashboardId: number;
}

export const Dashboard: FC<DashboardProps> = ({ dashboardId }) => {
  const { data, isLoading } = useDashboardQuery(dashboardId);

  const { mutate: updateWidgetOrder } =
    useUpdateWidgetOrderMutation(dashboardId);

  const { fields: projectFields, loading } =
    useProjectFieldsAsFilterableFields();

  const fields = projectFields.filter(
    (x) => !excludedProjectFieldsForFilter.includes(x.fieldName),
  );

  const transformer = useMemo(
    () => getApiFilterTransformer(keyBy(fields, 'fieldName')),
    [fields],
  );

  const lookup = keyBy(projectFields, 'fieldName');

  const { selectFilter, filter, clearFilters } = useFilters(
    lookup,
    loading ? 'loading' : 'loaded',
  );

  const dashboardSimpleFilter = useMemo(
    () => transformer.to(filter),
    [filter, transformer],
  );

  const [chartWidgetsOrder, setChartWidgetsOrder] = useState<string[]>([]);
  const [cardWidgetsOrder, setCardWidgetsOrder] = useState<string[]>([]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const widgets = useMemo(
    () =>
      (data?.widgets ?? []).filter(
        (widget) => !widget.deleted && widget.enabled,
      ),
    [data],
  );

  const numberTypeWidgetInitalOrder = useMemo(
    () =>
      (widgets ?? [])
        .filter((widget) => byNumberTypeWidget(widget.widgetType))
        .sort((a, b) => a.createdAt - b.createdAt)
        .sort((a, b) => a.widgetOrder - b.widgetOrder)
        .map((widget) => String(widget.widgetId)),
    [widgets],
  );
  const chartTypeWidgetsIntialOrder = useMemo(
    () =>
      (widgets ?? [])
        .filter((widget) => !byNumberTypeWidget(widget.widgetType))
        .sort((a, b) => a.createdAt - b.createdAt)
        .sort((a, b) => a.widgetOrder - b.widgetOrder)
        .map((widget) => String(widget.widgetId)),
    [widgets],
  );

  useDeepCompareEffect(() => {
    setChartWidgetsOrder(chartTypeWidgetsIntialOrder);
  }, [chartTypeWidgetsIntialOrder]);

  useDeepCompareEffect(() => {
    setCardWidgetsOrder(numberTypeWidgetInitalOrder);
  }, [numberTypeWidgetInitalOrder]);

  const initialItemsMap = useMemo(() => {
    return keyBy(widgets, 'widgetId');
  }, [widgets]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      const widget = initialItemsMap[active.id];

      const isChartOrCard = Object.values(NumberWidgetTypes).includes(
        widget.widgetType,
      );

      const setOrder = isChartOrCard
        ? setCardWidgetsOrder
        : setChartWidgetsOrder;

      const order = isChartOrCard ? cardWidgetsOrder : chartWidgetsOrder;

      if (active.id !== over?.id) {
        const oldIndex = order.indexOf(active.id);
        const newIndex = order.indexOf(over!.id);
        const updatedOrder = arrayMove(order, oldIndex, newIndex);

        const orderWithId = updatedOrder.map((id, index) => ({
          widgetId: Number(id),
          widgetOrder: index,
        }));

        updateWidgetOrder(orderWithId);
        setOrder(updatedOrder);
      }

      setActiveId(null);
    },
    [cardWidgetsOrder, chartWidgetsOrder, initialItemsMap, updateWidgetOrder],
  );

  return (
    <>
      <DashBoardFilter
        filter={filter}
        fields={fields}
        onChange={selectFilter}
        clearFilter={clearFilters}
        dashboardId={Number(dashboardId)}
        loading={loading}
      />

      <Wrapper id={DASHBOARD_CONTAINER_ID}>
        {!isLoading &&
          !chartWidgetsOrder.length &&
          !cardWidgetsOrder.length && <EmptyState />}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Wrapper.Item>
            <SortableContext
              items={cardWidgetsOrder}
              strategy={rectSortingStrategy}
            >
              <Row gutter={[16, 16]}>
                {cardWidgetsOrder.map((cardId) => {
                  const widget = initialItemsMap[cardId];

                  if (!widget) {
                    return null;
                  }

                  return (
                    <Col
                      key={widget.widgetId}
                      xs={24}
                      md={12}
                      lg={6}
                      xl={4}
                      xxl={3}
                    >
                      <DraggableComponent
                        Component={NumberWidget}
                        dashboardSimpleFilter={dashboardSimpleFilter}
                        dashboardFilter={filter}
                        widget={widget}
                        id={cardId}
                      />
                    </Col>
                  );
                })}
              </Row>
            </SortableContext>
          </Wrapper.Item>
          <Wrapper.Item>
            <SortableContext
              items={chartWidgetsOrder}
              strategy={rectSortingStrategy}
            >
              <Row gutter={[16, 16]}>
                {chartWidgetsOrder.map((chartId) => {
                  const widget = initialItemsMap?.[chartId];

                  if (!widget) {
                    return null;
                  }

                  return (
                    <Col
                      key={widget.widgetId}
                      xs={24}
                      md={12}
                      lg={12}
                      xl={12}
                      xxl={8}
                    >
                      <DraggableComponent
                        Component={ChartWidget}
                        dashboardSimpleFilter={dashboardSimpleFilter}
                        dashboardFilter={filter}
                        widget={widget}
                        id={chartId}
                      />
                    </Col>
                  );
                })}
              </Row>
              <DragOverlay>
                {activeId && initialItemsMap[activeId] ? (
                  <ChartDndOverLay widget={initialItemsMap[activeId]} />
                ) : null}
              </DragOverlay>
            </SortableContext>
          </Wrapper.Item>
        </DndContext>
      </Wrapper>
    </>
  );
};
